import Fuse from 'fuse.js';

/**
 * Calculates a fuzzy match score between a query and a candidate string using Fuse.js.
 * Lower scores indicate a better match.
 * Returns Infinity if the match score is too poor (above threshold).
 *
 * @param {string} query - The search query string.
 * @param {string} candidate - The string to score.
 * @return {number} A numeric score where a lower score means a better match.
 */
export function fuzzyScore(query: string, candidate: string): number {
  const fuse = new Fuse([candidate], {
    includeScore: true,
    threshold: 0.4, // 0 = perfect match, 1 = match anything
    ignoreLocation: true,
  });

  const result = fuse.search(query);

  if (result.length === 0 || result[0].score === undefined) {
    return Infinity;
  }

  // Fuse.js returns scores between 0 (perfect) and 1 (poor)
  // Multiply by 100 to get a more readable scale
  return result[0].score * 100;
}

/**
 * Performs a fuzzy search on an array of strings using Fuse.js.
 *
 * @param {string} query - The search query string to match against candidates.
 * @param {string[]} candidates - The list of candidate strings to search through.
 * @return {string[]} An array of candidate strings that match the query,
 *          sorted by the best fuzzy match.
 */
export function fuzzySearch(query: string, candidates: string[]): string[] {
  const fuse = new Fuse(candidates, {
    includeScore: true,
    threshold: 0.4,
    ignoreLocation: true,
  });

  const results = fuse.search(query);

  return results.map(result => result.item);
}
